/**
 * Calendar Preferences Manager
 *
 * Manages calendar visibility preferences (show/hide toggle).
 * Note: Calendar association/linking is handled separately via UserProfile.linkedCalendars
 * in the database.
 *
 * Responsibilities:
 * - Calendar visibility (show/hide in calendar view)
 * - New calendar detection
 * - Preference persistence to AsyncStorage
 *
 * Follows manager pattern: centralizes business logic separate from UI.
 */

import type { DeviceCalendar } from '@/lib/calendar/DeviceCalendarService';
import { ScopedAsyncStorage } from '@/lib/storage';
import type {
  CalendarPreference,
  CalendarPreferences,
  CalendarPreferencesStorage,
  CalendarSetupState,
  NewCalendarsDetectionResult,
  UpdateCalendarPreferenceInput,
} from '@/types/calendar-preferences';

const STORAGE_KEY = 'calendarPreferences';
const CURRENT_VERSION = '1.0.0';

/**
 * CalendarPreferencesManager
 *
 * Handles all calendar visibility preference operations including storage,
 * filtering, and migration.
 */
export class CalendarPreferencesManager {
  private readonly storage: ScopedAsyncStorage;

  /**
   * Create a new CalendarPreferencesManager instance
   * @param storage - Scoped storage instance
   */
  constructor(storage: ScopedAsyncStorage) {
    this.storage = storage;
  }

  /**
   * Load preferences from storage
   */
  async loadPreferences(): Promise<CalendarPreferencesStorage> {
    try {
      const stored = await this.storage.getItem(STORAGE_KEY);

      if (!stored) {
        return this.createDefaultPreferencesStorage();
      }

      const parsed = JSON.parse(stored) as CalendarPreferencesStorage;

      // Validate version and migrate if needed
      if (parsed.version !== CURRENT_VERSION) {
        return await this.migratePreferences(parsed);
      }

      return parsed;
    } catch (err) {
      console.error(
        '[CalendarPreferencesManager] Failed to load preferences:',
        err,
      );
      return this.createDefaultPreferencesStorage();
    }
  }

  /**
   * Save preferences to storage
   */
  async savePreferences(
    storage: CalendarPreferencesStorage,
  ): Promise<void> {
    try {
      await this.storage.setItem(STORAGE_KEY, JSON.stringify(storage));
    } catch (err) {
      console.error(
        '[CalendarPreferencesManager] Failed to save preferences:',
        err,
      );
      throw new Error('Failed to save calendar preferences');
    }
  }

  /**
   * Check if setup wizard has been completed
   */
  async hasCompletedSetup(): Promise<boolean> {
    const storage = await this.loadPreferences();
    return storage.setup.setupCompleted;
  }

  /**
   * Mark setup wizard as completed
   */
  async markSetupCompleted(): Promise<void> {
    const storageData = await this.loadPreferences();
    storageData.setup = {
      setupCompleted: true,
      completedAt: new Date().toISOString(),
    };
    await this.savePreferences(storageData);
  }


  /**
   * Initialize preferences for calendars
   * All calendars default to visible
   */
  async initializePreferences(
    calendars: DeviceCalendar[],
  ): Promise<CalendarPreferences> {
    const now = new Date().toISOString();
    const preferences: CalendarPreferences = {};

    calendars.forEach((calendar) => {
      preferences[calendar.id] = {
        isVisible: true, // Default: all visible
        lastSeenAt: now,
      };
    });

    return preferences;
  }

  /**
   * Update a single calendar preference
   */
  async updateCalendarPreference(
    input: UpdateCalendarPreferenceInput,
  ): Promise<void> {
    const storageData = await this.loadPreferences();
    const existing = storageData.preferences[input.calendarId];

    storageData.preferences[input.calendarId] = {
      isVisible:
        input.isVisible !== undefined
          ? input.isVisible
          : existing?.isVisible ?? true,
      lastSeenAt:
        existing?.lastSeenAt ?? new Date().toISOString(),
    };

    await this.savePreferences(storageData);
  }

  /**
   * Batch update calendar preferences
   */
  async batchUpdatePreferences(
    updates: UpdateCalendarPreferenceInput[],
  ): Promise<void> {
    const storageData = await this.loadPreferences();
    const now = new Date().toISOString();

    updates.forEach((input) => {
      const existing = storageData.preferences[input.calendarId];

      storageData.preferences[input.calendarId] = {
        isVisible:
          input.isVisible !== undefined
            ? input.isVisible
            : existing?.isVisible ?? true,
        lastSeenAt: existing?.lastSeenAt ?? now,
      };
    });

    await this.savePreferences(storageData);
  }

  /**
   * Detect new calendars that don't exist in preferences
   */
  async detectNewCalendars(
    currentCalendars: DeviceCalendar[],
  ): Promise<NewCalendarsDetectionResult> {
    const storageData = await this.loadPreferences();
    const existingIds = new Set(Object.keys(storageData.preferences));
    const currentIds = new Set(
      currentCalendars.map((cal) => cal.id),
    );

    const newCalendarIds = currentCalendars
      .filter((cal) => !existingIds.has(cal.id))
      .map((cal) => cal.id);

    const removedCalendarIds = Array.from(existingIds).filter(
      (id) => !currentIds.has(id),
    );

    return {
      newCalendarIds,
      allCalendarIds: Array.from(currentIds),
      removedCalendarIds,
    };
  }

  /**
   * Add preferences for newly detected calendars
   */
  async addNewCalendars(newCalendars: DeviceCalendar[]): Promise<void> {
    const storageData = await this.loadPreferences();
    const now = new Date().toISOString();

    newCalendars.forEach((calendar) => {
      if (!storageData.preferences[calendar.id]) {
        storageData.preferences[calendar.id] = {
          isVisible: true, // Default: visible
          lastSeenAt: now,
        };
      }
    });

    await this.savePreferences(storageData);
  }

  /**
   * Remove preferences for calendars that no longer exist
   */
  async removeOldCalendars(calendarIds: string[]): Promise<void> {
    const storageData = await this.loadPreferences();

    calendarIds.forEach((id) => {
      delete storageData.preferences[id];
    });

    await this.savePreferences(storageData);
  }

  /**
   * Filter calendars by visibility preference
   * Returns only calendars that are marked as visible
   */
  filterVisibleCalendars(
    calendars: DeviceCalendar[],
    preferences: CalendarPreferences,
  ): DeviceCalendar[] {
    return calendars.filter((calendar) => {
      const pref = preferences[calendar.id];
      // Default to visible if no preference exists
      return pref?.isVisible ?? true;
    });
  }


  /**
   * Migrate preferences from old version to current version
   */
  private async migratePreferences(
    old: CalendarPreferencesStorage,
  ): Promise<CalendarPreferencesStorage> {
    // Currently no migrations needed, just update version
    const migrated: CalendarPreferencesStorage = {
      ...old,
      version: CURRENT_VERSION,
    };

    await this.savePreferences(migrated);
    return migrated;
  }

  /**
   * Create default preferences storage structure
   */
  private createDefaultPreferencesStorage(): CalendarPreferencesStorage {
    return {
      setup: {
        setupCompleted: false,
        completedAt: null,
      },
      preferences: {},
      version: CURRENT_VERSION,
    };
  }

  /**
   * Migrate from old selectedCalendarIds storage to new preferences
   * This helps users transition from the old system
   */
  async migrateFromLegacySelection(
    calendars: DeviceCalendar[],
    selectedIds: string[],
  ): Promise<void> {
    const storageData = await this.loadPreferences();

    // Only migrate if preferences are empty
    if (Object.keys(storageData.preferences).length > 0) {
      return;
    }

    const preferences = await this.initializePreferences(calendars);

    // Keep the old selection state as visibility preference
    Object.keys(preferences).forEach((calendarId) => {
      preferences[calendarId].isVisible = selectedIds.includes(calendarId);
    });

    storageData.preferences = preferences;
    await this.savePreferences(storageData);
  }
}
